import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Upload } from "lucide-react";
import Button from "../forms/Button";
import {
  taskDiscussionService,
  apiErrorMessage,
} from "../../api/taskBoardService";
import { roleIn } from "./boardState";
import {
  ACCEPTED_FILES,
  AttachmentTile,
  ImagePreviewModal,
  checkFile,
} from "./attachments";

/**
 * Left-hand "Attachments" section of the task details: files attached
 * directly to the task (comment files live with their comments). Kept in
 * sync by the task's realtime activity events.
 */
const TaskAttachments = ({ task, access, subscribeActivity }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(null);
  const input = useRef(null);

  const load = useCallback(async () => {
    try {
      setItems((await taskDiscussionService.attachments(task._id)).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load attachments"));
    } finally {
      setLoading(false);
    }
  }, [task._id]);

  useEffect(() => {
    load();
  }, [load]);

  // Live: new task-level files appear, removed ones disappear (deduped by id).
  useEffect(
    () =>
      subscribeActivity?.((event) => {
        if (event.type === "resync") return load();
        if (event.taskId !== task._id) return;
        setItems((prev) => {
          let next = prev;
          (event.activities || []).forEach((a) => {
            const att = a.attachment;
            if (
              a.type === "attachment_added" &&
              att &&
              !att.comment &&
              !att.deletedAt &&
              !next.some((x) => x._id === att._id)
            ) {
              next = [att, ...next];
            }
          });
          (event.attachments || []).forEach((att) => {
            if (att.deletedAt) next = next.filter((x) => x._id !== att._id);
          });
          return next;
        });
      }),
    [subscribeActivity, task._id, load],
  );

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (input.current) input.current.value = "";
    if (!files.length) return;
    const problem = files.map(checkFile).find(Boolean);
    if (problem) return setError(problem);
    setError("");
    setProgress(0);
    try {
      const res = await taskDiscussionService.uploadAttachments(
        task._id,
        files,
        (e) =>
          setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      );
      setItems((prev) => [
        ...res.data.filter((a) => !prev.some((x) => x._id === a._id)),
        ...prev,
      ]);
    } catch (err) {
      setError(apiErrorMessage(err, "Upload failed"));
    } finally {
      setProgress(null);
    }
  };

  const remove = async (attachment) => {
    await taskDiscussionService.removeAttachment(attachment);
    setItems((prev) => prev.filter((x) => x._id !== attachment._id));
  };

  // Uploader, squad lead or admin (the API enforces the same rule).
  const canRemove = (a) =>
    a.uploadedBy === access.userId ||
    access.isAdmin ||
    roleIn(access, task.squad) === "SQUAD_LEAD";

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2.5 font-display text-[19px] font-semibold text-ink whitespace-nowrap">
          <Paperclip className="w-5 h-5 text-muted" /> Attachments
          {items.length > 0 && (
            <span className="font-mono text-xs font-normal text-faint">
              ({items.length})
            </span>
          )}
        </h3>
        <input
          ref={input}
          type="file"
          multiple
          accept={ACCEPTED_FILES}
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => input.current?.click()}
          disabled={progress !== null}
        >
          <Upload className="w-4 h-4" />{" "}
          {progress !== null ? `Uploading ${progress}%` : "Add"}
        </Button>
      </div>

      {progress !== null && (
        <div
          className="h-1.5 rounded-full bg-raise overflow-hidden"
          role="progressbar"
          aria-valuenow={progress}
        >
          <div
            className="h-full bg-grad transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      {loading ? (
        <div className="h-14 rounded-xl bg-raise animate-pulse" />
      ) : items.length === 0 ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            upload(e.dataTransfer.files);
          }}
          disabled={progress !== null}
          className="w-full rounded-[14px] border border-dashed border-line2 px-4 py-6 text-[13.5px] text-faint text-center hover:border-accent-line hover:text-muted transition-colors"
        >
          No files yet. Supported: Excel, CSV, images and PDF (up to 10 MB).
        </button>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((a) => (
            <AttachmentTile
              key={a._id}
              attachment={a}
              canRemove={canRemove(a)}
              onRemove={remove}
              onPreview={setPreview}
              onError={(err) => setError(apiErrorMessage(err, "Action failed"))}
            />
          ))}
        </div>
      )}

      <ImagePreviewModal
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </section>
  );
};

export default TaskAttachments;
